import { useEffect, useMemo, useState } from 'react';
import { AxiosResponse } from 'axios';
import Joi from 'joi';
import _, { noop } from 'lodash';

import { SaveStatus } from '../../shared/SavePanel';
import { ToggleSwitch } from '../../shared/ToggleSwitch/ToggleSwitch';
import { PackDrawerMedia } from '../PackDrawerMedia/PackDrawerMedia';
import { PackSelect } from '../PackSelect/PackSelect';
import { LabelInput, SavePanel, Section } from '../../shared';

import { useProcessedMediaStatuses } from '../../hooks/medias';
import { usePacksList } from '../../hooks/packs';

import { ImageFileUploading } from '../../utils/upload';

import { MediaType } from '../../types/enums/mediatype.enum';

import { Media } from '../../types/mediaTypes';
import { PacksDrawerRecord } from '../../types/packsDrawerTypes';

import styles from './PacksDrawerDetails.module.scss';

type MediaOrImageFileUploading = Media | (ImageFileUploading & { id?: number });

export type PacksDrawerFormState = {
    packId: string;
    active: boolean;
    isFree: boolean;
    thumbnailMediaId: number | null;
};

export function createEmptyPacksDrawerForm(): PacksDrawerFormState {
    return {
        packId: '',
        active: true,
        isFree: false,
        thumbnailMediaId: null,
    };
}

export function packsDrawerFormFromRecord(
    record: PacksDrawerRecord,
): PacksDrawerFormState {
    return {
        packId: record.packId,
        active: record.active,
        isFree: record.isFree,
        thumbnailMediaId: record.thumbnail?.id ?? null,
    };
}

type PacksDrawerDetailsProps = {
    isNew: boolean;
    initialForm: PacksDrawerFormState;
    initialThumbnail?: Media | null;
    isSaving: boolean;
    onSave: (form: PacksDrawerFormState) => Promise<boolean>;
    onDelete?: () => Promise<void>;
};

export const PacksDrawerDetails = (props: PacksDrawerDetailsProps) => {
    const {
        isNew,
        initialForm,
        initialThumbnail,
        isSaving,
        onSave,
        onDelete = () => Promise.resolve(),
    } = props;

    const [form, setForm] = useState<PacksDrawerFormState>(initialForm);
    const [savedForm, setSavedForm] =
        useState<PacksDrawerFormState>(initialForm);
    const [thumbnail, setThumbnail] = useState<
        MediaOrImageFileUploading | undefined
    >(initialThumbnail ?? undefined);
    const [isSavedStatusShown, setIsSavedStatusShown] = useState(false);
    const { onProcessingComplete, onProcessingError } =
        useProcessedMediaStatuses();
    const { packs } = usePacksList({ enabled: true });

    const selectedPack = useMemo(
        () => packs?.find((pack) => pack.packId === form.packId),
        [packs, form.packId],
    );

    const packName = selectedPack
        ? selectedPack.displayName?.en || selectedPack.name
        : '';

    const hasUnsavedChanges = useMemo(
        () => !_.isEqual(form, savedForm),
        [form, savedForm],
    );

    const setHasUnsavedChanges = (hasUnsavedChanges: boolean) => {
        localStorage.setItem('hasUnsavedChanges', String(hasUnsavedChanges));
    };

    useEffect(() => {
        setHasUnsavedChanges(hasUnsavedChanges);
    }, [hasUnsavedChanges]);

    useEffect(() => {
        return () => {
            setHasUnsavedChanges(false);
        };
    }, []);

    useEffect(() => {
        if (isSavedStatusShown) {
            setTimeout(() => {
                setIsSavedStatusShown(false);
            }, 2000);
        }
    }, [isSavedStatusShown]);

    const onPackIdChange = (packId: string) => {
        setForm((form) => ({ ...form, packId }));
    };

    const onActiveToggle = () => {
        setForm((form) => ({ ...form, active: !form.active }));
    };

    const onIsFreeToggle = () => {
        setForm((form) => ({ ...form, isFree: !form.isFree }));
    };

    const onPhotosSelected = (
        _type: MediaType,
        files: ImageFileUploading[],
    ) => {
        if (!files.length) {
            return;
        }
        setThumbnail(files[0]);
    };

    const onPhotosWasUploaded = (
        _type: MediaType,
        _file: File,
        res: AxiosResponse<{ uploaded: Media }>,
    ) => {
        setThumbnail(
            _.assign(res.data.uploaded as Media, {
                uploading: false,
            }),
        );
    };

    const thumbnailItems = [thumbnail]
        .filter((m) => !!m)
        .slice(0, 1) as MediaOrImageFileUploading[];

    const thumbnailMediaId =
        thumbnail && 'id' in thumbnail ? (thumbnail.id ?? null) : null;

    const formSchema = Joi.object({
        packId: Joi.string().required(),
        active: Joi.boolean().required(),
        isFree: Joi.boolean().required(),
        thumbnailMediaId: Joi.number().integer().allow(null),
    });
    const validationResults = {
        errors: formSchema.validate(form),
        warnings: {} as Joi.ValidationResult,
    };

    const getSaveStatus = (): SaveStatus => {
        if (isSaving) {
            return SaveStatus.SAVING;
        }
        if (hasUnsavedChanges) {
            return SaveStatus.HAS_CHANGED;
        }
        return SaveStatus.NO_CHANGES;
    };

    const save = async () => {
        if (isSaving) {
            return;
        }

        const submittedForm = {
            ...form,
            thumbnailMediaId,
        };
        const isSaved = await onSave(submittedForm);

        if (isSaved) {
            setSavedForm(submittedForm);
            setHasUnsavedChanges(false);
            setIsSavedStatusShown(true);
        }
    };

    return (
        <>
            <Section
                title={
                    isNew
                        ? `New Packs Drawer Entry: ${form.packId}`
                        : form.packId
                }
            >
                <div className={styles.field}>
                    <PackSelect
                        label="Pack"
                        value={form.packId}
                        onChange={onPackIdChange}
                        disabled={!isNew}
                    />
                </div>
                <div className={styles.nameField}>
                    <LabelInput
                        size="semiwide"
                        name="name"
                        value={packName}
                        label="Name"
                        disabled
                        onChange={noop}
                    />
                </div>
                <div className={styles.toggleRow}>
                    <ToggleSwitch
                        checked={form.active}
                        onToggle={onActiveToggle}
                    />
                    <span>Active</span>
                </div>
                <div className={styles.toggleRow}>
                    <ToggleSwitch
                        checked={form.isFree}
                        onToggle={onIsFreeToggle}
                    />
                    <span>Free</span>
                </div>
            </Section>
            <Section title="Thumbnail">
                <PackDrawerMedia
                    type={MediaType.PACK_DRAWER_THUMB}
                    header="Packs drawer thumbnail (PNG)"
                    filename={form.packId || 'packs-drawer'}
                    items={thumbnailItems}
                    onMediaSelect={onPhotosSelected}
                    onMediaWasUploaded={onPhotosWasUploaded}
                    onMediaDragEnd={noop}
                    buttonLabel={thumbnailItems.length ? 'Replace' : 'Add new'}
                    onProcessingComplete={onProcessingComplete}
                    onProcessingError={onProcessingError}
                />
            </Section>
            <SavePanel
                title={`Working with: ${form.packId || 'no pack'}`}
                validationResults={validationResults}
                item={form}
                save={save}
                showDelete={!isNew}
                saveStatus={
                    isSavedStatusShown ? SaveStatus.SAVED : getSaveStatus()
                }
                onDelete={onDelete}
            />
        </>
    );
};
